import type {
  GestorDashboardBreakdownRow,
  GestorDashboardCollaboratorView,
  GestorDashboardOverview,
  GestorDashboardPayload,
  GestorDashboardSummary,
  GestorDashboardTimeEntry,
  GestorDashboardFinancialMetrics,
} from '@/components/Gestores/gestorDashboardTypes'

import { matchFilterValues } from '@/components/Gestores/gestorDashboardFilterListUtils'
import { filterEntriesWithinPeriod } from '@/components/Gestores/gestorDashboardPeriodUtils'

type ClientFilters = {
  workspaces?: string[]
  executors?: string[]
  workspace?: string
  executor?: string
  taskType?: string
  phase?: string
  search?: string
}

function aggregateByField(
  entries: GestorDashboardTimeEntry[],
  pick: (entry: GestorDashboardTimeEntry) => { key: string; label: string } | null,
): GestorDashboardBreakdownRow[] {
  const map = new Map<string, GestorDashboardBreakdownRow>()
  for (const entry of entries) {
    const picked = pick(entry)
    if (!picked) continue
    const current = map.get(picked.key) ?? { ...picked, effortMinutes: 0 }
    current.effortMinutes += entry.effortMinutes
    map.set(picked.key, current)
  }
  return [...map.values()].sort((a, b) => b.effortMinutes - a.effortMinutes)
}

export function filterDashboardEntries(
  entries: GestorDashboardTimeEntry[],
  filters: ClientFilters,
): GestorDashboardTimeEntry[] {
  const workspaces = filters.workspaces ?? (filters.workspace ? [filters.workspace] : [])
  const executors = filters.executors ?? (filters.executor ? [filters.executor] : [])
  const taskType = filters.taskType?.trim().toLowerCase()
  const phase = filters.phase?.trim().toLowerCase()
  const search = filters.search?.trim().toLowerCase()

  return entries.filter((entry) => {
    if (!matchFilterValues(workspaces, entry.workspace)) return false
    if (!matchFilterValues(executors, entry.executor)) return false
    if (taskType && !(entry.taskType ?? '').toLowerCase().includes(taskType)) return false
    if (phase && !(entry.phase ?? '').toLowerCase().includes(phase)) return false
    if (!search) return true

    const haystack = [
      entry.titleOrComment,
      entry.task,
      entry.workspace,
      entry.executor,
      entry.taskType,
      entry.phase,
      entry.ticket,
      entry.campaign,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(search)
  })
}

function summarizeEntries(entries: GestorDashboardTimeEntry[]): GestorDashboardSummary {
  const productive = entries.filter((entry) => entry.productive)
  return {
    totalEffortMinutes: entries.reduce((sum, entry) => sum + entry.effortMinutes, 0),
    productiveEffortMinutes: productive.reduce((sum, entry) => sum + entry.effortMinutes, 0),
    executorCount: new Set(entries.map((entry) => entry.executorId ?? entry.executor).filter(Boolean)).size,
    workspaceCount: new Set(entries.map((entry) => entry.workspaceId ?? entry.workspace).filter(Boolean)).size,
    entryCount: entries.length,
    byExecutor: aggregateByField(entries, (entry) =>
      entry.executor ? { key: entry.executorId ?? entry.executor, label: entry.executor } : null,
    ),
    byWorkspace: aggregateByField(entries, (entry) =>
      entry.workspace ? { key: String(entry.workspaceId ?? entry.workspace), label: entry.workspace } : null,
    ),
    byTaskType: aggregateByField(entries, (entry) =>
      entry.taskType ? { key: String(entry.taskTypeId ?? entry.taskType), label: entry.taskType } : null,
    ),
  }
}

function countByField(
  entries: GestorDashboardTimeEntry[],
  pick: (entry: GestorDashboardTimeEntry) => { key: string; label: string } | null,
): Array<{ key: string; label: string; count: number }> {
  const map = new Map<string, { key: string; label: string; count: number }>()
  for (const entry of entries) {
    const picked = pick(entry)
    if (!picked) continue
    const current = map.get(picked.key) ?? { ...picked, count: 0 }
    current.count += 1
    map.set(picked.key, current)
  }
  return [...map.values()].sort((a, b) => b.count - a.count)
}

function buildCollaboratorView(entries: GestorDashboardTimeEntry[]): GestorDashboardCollaboratorView {
  const dailyMap = new Map<string, { date: string; effortMinutes: number }>()
  const taskEffortMap = new Map<
    string,
    { key: string; taskId: number | null; task: string; executor: string | null; effortMinutes: number }
  >()

  for (const entry of entries) {
    if (entry.date) {
      const current = dailyMap.get(entry.date) ?? { date: entry.date, effortMinutes: 0 }
      current.effortMinutes += entry.effortMinutes
      dailyMap.set(entry.date, current)
    }
    if (!entry.task) continue
    const key = String(entry.taskId ?? entry.task)
    const current = taskEffortMap.get(key) ?? {
      key,
      taskId: entry.taskId ?? null,
      task: entry.task,
      executor: entry.executor,
      effortMinutes: 0,
    }
    current.effortMinutes += entry.effortMinutes
    taskEffortMap.set(key, current)
  }

  return {
    byExecutor: aggregateByField(entries, (entry) =>
      entry.executor ? { key: entry.executorId ?? entry.executor, label: entry.executor } : null,
    ),
    tasksByExecutor: countByField(entries, (entry) =>
      entry.executor ? { key: entry.executorId ?? entry.executor, label: entry.executor } : null,
    ),
    dailyHours: [...dailyMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
    topTasksByEffort: [...taskEffortMap.values()]
      .sort((a, b) => b.effortMinutes - a.effortMinutes)
      .slice(0, 10),
  }
}

function buildClientsView(entries: GestorDashboardTimeEntry[]) {
  const byWorkspace = aggregateByField(entries, (entry) =>
    entry.workspace ? { key: String(entry.workspaceId ?? entry.workspace), label: entry.workspace } : null,
  )
  return {
    byWorkspace,
    byPhase: aggregateByField(entries, (entry) =>
      entry.phase ? { key: String(entry.phaseId ?? entry.phase), label: entry.phase } : null,
    ),
    topConsumers: byWorkspace.slice(0, 8),
    bottomConsumers: [...byWorkspace].reverse().slice(0, 8),
  }
}

function buildOverview(entries: GestorDashboardTimeEntry[], base: GestorDashboardOverview): GestorDashboardOverview {
  const uniqueTasks = new Set(entries.map((entry) => entry.taskId).filter(Boolean))
  const totalEffort = entries.reduce((sum, entry) => sum + entry.effortMinutes, 0)
  return {
    ...base,
    averageEffortMinutes: uniqueTasks.size > 0 ? Math.round(totalEffort / uniqueTasks.size) : 0,
  }
}

export function applyDashboardClientFilters(
  base: GestorDashboardPayload,
  filters: ClientFilters,
  financial?: GestorDashboardFinancialMetrics | null,
): GestorDashboardPayload {
  const periodEntries = filterEntriesWithinPeriod(base.entries, base.period.createdFrom, base.period.createdTo)
  const filtered = filterDashboardEntries(periodEntries, filters)
  return {
    ...base,
    summary: summarizeEntries(filtered),
    entries: filtered,
    overview: buildOverview(filtered, base.overview),
    collaborator: buildCollaboratorView(filtered),
    clients: buildClientsView(filtered),
    financial: financial ?? base.financial,
  }
}

export function buildDashboardFilterOptions(base: GestorDashboardPayload) {
  return {
    executors: base.summary.byExecutor.map((row) => ({ value: row.label, label: row.label })),
    workspaces: base.summary.byWorkspace.map((row) => ({ value: row.label, label: row.label })),
    taskTypes: base.summary.byTaskType.map((row) => ({ value: row.label, label: row.label })),
  }
}
